import { useCallback } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';

/**
 * Spec 081b — `packages.*` strings for the partner Promotions → Packages screens.
 *
 * Same approach as offersI18n: the portal's `t()` reads the admin DB first; until the
 * `packages.*` keys are seeded there, these bundled EN/AR strings are used.
 * R2–R6 / R9 messages are the requirement's exact copy.
 */
type Dict = Record<string, string>;

const en: Dict = {
  'packages.tab.offers': 'Offers',
  'packages.tab.packages': 'Packages',
  'packages.tab.label': 'Promotion type',
  'packages.title': 'Packages',
  'packages.subtitle': 'Bundle 2 to 6 of your services at one price. Garagat reviews every new package before it goes live.',
  'packages.new': 'New package',
  'packages.loading': 'Loading packages…',
  'packages.loadFailed': 'We could not load your packages. Please try again.',
  'packages.retry': 'Try again',
  'packages.empty.title': 'No packages yet',
  'packages.empty.body': 'Bundle services customers book together, like a wash, a polish and an interior clean, at one package price.',
  'packages.empty.cta': 'Create your first package',
  'packages.empty.needServices': 'You need at least 2 active services to build a package. Add or activate services first.',
  'packages.empty.goServices': 'Go to services',
  'packages.filter.label': 'Status',
  'packages.filter.all': 'All',
  'packages.filter.ARCHIVED': 'Archived',
  'packages.filter.empty': 'No packages with this status.',
  'packages.itemsCount': '{n} services',
  'packages.col.order': 'Order',
  'packages.col.name': 'Package',
  'packages.col.price': 'Price',
  'packages.col.status': 'Status',
  'packages.col.window': 'Runs',
  'packages.col.actions': 'Actions',
  'packages.reorder.hint': 'Running packages show on your garage profile in this order.',
  'packages.reorder.disabledHint': 'Show all statuses to change the order.',
  'packages.reorder.failed': 'We could not save the new order. Please try again.',
  'packages.status.DRAFT': 'Draft',
  'packages.status.PENDING_APPROVAL': 'Pending approval',
  'packages.status.SCHEDULED': 'Scheduled',
  'packages.status.LIVE': 'Live',
  'packages.status.PAUSED': 'Paused',
  'packages.status.ENDED': 'Ended',
  'packages.status.REJECTED': 'Rejected',
  'packages.status.SUSPENDED': 'Suspended',
  'packages.mode.FIXED': 'Fixed price',
  'packages.mode.PERCENT': '% off',
  'packages.price.percentOff': '{pct}% off',
  'packages.window.always': 'Until you end it',
  'packages.unavailable.badge': 'Not available',
  'packages.reason.INACTIVE': 'the service is inactive',
  'packages.reason.PENDING_APPROVAL': 'the service is waiting for approval',
  'packages.reason.REJECTED': 'the service was rejected',
  'packages.reason.BLOCKED': 'the service is blocked by Garagat',
  'packages.reason.HIDDEN': 'the service is hidden',
  'packages.reason.NOT_PRICED': 'the service has no price',
  'packages.reason.CHOICE_REMOVED': 'a fixed choice was removed',
  'packages.reason.PRICE_NOT_BELOW_TOTAL': 'the package price is no longer below the services’ total',
  'packages.reason.GARAGE_BLOCKED': 'your garage is blocked',
  'packages.reason.OTHER': 'it is not available',
  'packages.rejectReason.CONTACT_DETAILS': 'Contact details or an outside link on the image',
  'packages.rejectReason.PRICE_MISMATCH': 'The price on the image differs from the package price',
  'packages.rejectReason.UNREADABLE_IMAGE': 'Unclear or low-quality image',
  'packages.rejectReason.MISLEADING_CLAIM': 'Misleading claim',
  'packages.rejectReason.COMPETITOR_BRANDING': 'Competitor branding',
  'packages.rejectReason.ARABIC_MISSING': 'Arabic text missing',
  'packages.rejectReason.OTHER': 'Other reason',
  'packages.action.edit': 'Edit',
  'packages.action.view': 'View',
  'packages.action.pause': 'Pause',
  'packages.action.resume': 'Resume',
  'packages.action.end': 'End now',
  'packages.action.archive': 'Archive',
  'packages.action.withdraw': 'Withdraw',
  'packages.confirm.cancel': 'Cancel',
  'packages.confirm.pause.title': 'Pause this package?',
  'packages.confirm.pause.body': 'Customers stop seeing it and cannot book it. You can resume it any time.',
  'packages.confirm.resume.title': 'Resume this package?',
  'packages.confirm.resume.body': 'Customers can see and book the package again.',
  'packages.confirm.end.title': 'End this package now?',
  'packages.confirm.end.body': 'The package stops immediately and cannot be restarted. Bookings already made keep their price.',
  'packages.confirm.archive.title': 'Archive this package?',
  'packages.confirm.archive.body': 'It moves out of your list. You can still find it under the Archived filter.',
  'packages.confirm.withdraw.title': 'Withdraw from review?',
  'packages.confirm.withdraw.body': 'The package goes back to draft so you can change it and submit again.',
  'packages.toast.updated': 'Package updated',
  'packages.editor.newTitle': 'New package',
  'packages.editor.editTitle': 'Edit package',
  'packages.editor.back': 'Back to packages',
  'packages.editor.loadFailed': 'We could not load this package.',
  'packages.section.details': 'Details',
  'packages.section.cover': 'Cover image',
  'packages.section.services': 'Services',
  'packages.section.price': 'Price',
  'packages.section.window': 'Dates',
  'packages.field.nameEn': 'Name (English)',
  'packages.field.nameAr': 'Name (Arabic)',
  'packages.field.descriptionEn': 'Description (English)',
  'packages.field.descriptionAr': 'Description (Arabic)',
  'packages.field.optional': 'optional',
  'packages.field.startsAt': 'Starts on',
  'packages.field.endsAt': 'Ends on',
  'packages.hint.arabic': 'Arabic is optional. Customers using the app in Arabic see the English text when it is empty.',
  'packages.hint.window': 'Optional. Leave both empty to run the package until you pause or end it. Up to 90 days when both are set.',
  'packages.cover.label': 'Cover image',
  'packages.cover.hint': 'Shown on the package card in the app.',
  'packages.picker.search': 'Search your services',
  'packages.picker.selected': '{n} of 6 selected',
  'packages.picker.hint': 'Pick 2 to 6 of your active services.',
  'packages.picker.loading': 'Loading your services…',
  'packages.picker.loadFailed': 'We could not load your services.',
  'packages.picker.empty': 'You have no services yet.',
  'packages.picker.noMatch': 'No service matches your search.',
  'packages.picker.max': 'A package can have up to 6 services.',
  'packages.picker.remove': 'Remove {name}',
  'packages.picker.from': 'from',
  'packages.picker.unavailable': 'Can’t be added: {reason}',
  'packages.fixer.title': 'Choices for {name}',
  'packages.fixer.customerPicks': 'Customer picks',
  'packages.fixer.noOptions': 'No options: one price.',
  'packages.fixer.carSpecific': 'Car-specific',
  'packages.fixer.carSpecificHint': 'The price depends on the customer’s car, so this package uses a % off.',
  'packages.fixer.multiHint': 'Fix one or more, or leave it to the customer.',
  'packages.fixer.maxValues': 'You can fix up to 10 choices per service.',
  'packages.price.modeLabel': 'Pricing',
  'packages.price.modeHint.FIXED': 'Every choice is fixed, so you set one package price.',
  'packages.price.modeHint.PERCENT': 'Some choices are left to the customer, or the price depends on the car, so you set a % off.',
  'packages.price.originalTotal': 'Services total',
  'packages.price.packagePrice': 'Package price (AED)',
  'packages.price.saves': 'Customer saves {amount} ({pct}%)',
  'packages.price.percent': 'Discount (%)',
  'packages.price.percentHint': 'Between 5% and 70% off what the customer picks.',
  'packages.price.from': 'Customers see “from {amount}”',
  'packages.price.checking': 'Checking prices…',
  'packages.price.previewFailed': 'We could not check the prices right now. They are checked again when you save.',
  'packages.price.addServices': 'Add at least 2 services to see the total.',
  'packages.bar.saveDraft': 'Save draft',
  'packages.bar.saveChanges': 'Save changes',
  'packages.bar.submit': 'Submit for approval',
  'packages.bar.saving': 'Saving…',
  'packages.bar.saved': 'Draft saved',
  'packages.bar.changesSaved': 'Changes saved. Customers see them now.',
  'packages.bar.submitted': 'Submitted for approval',
  'packages.bar.withdrawn': 'Withdrawn. The package is a draft again.',
  'packages.bar.submitNeedsItems': 'Add at least 2 services to submit.',
  'packages.notice.pending': 'Waiting for Garagat’s approval. Withdraw it if you need to change something.',
  'packages.notice.rejected': 'Garagat did not approve this package',
  'packages.notice.rejectedHint': 'Fix the points below and submit again.',
  'packages.notice.suspended': 'Garagat suspended this package',
  'packages.notice.suspendedHint': 'Customers cannot see it. Edit it and submit again.',
  'packages.notice.running': 'This package is {status}. Changes you save apply right away, after the same checks.',
  'packages.notice.ended': 'This package has ended and stays in your records.',
  'packages.notice.unavailable': 'Customers cannot see or book this package right now: {reason}. Fix it to make it available again.',
  'packages.adminEdited': 'Last edited by Garagat on {date}',
  'packages.preview.title': 'App preview',
  'packages.preview.language': 'Preview language',
  'packages.preview.theme': 'Preview theme',
  'packages.preview.en': 'English',
  'packages.preview.ar': 'Arabic',
  'packages.preview.light': 'Light',
  'packages.preview.dark': 'Dark',
  'packages.err.nameRequired': 'Enter an English name.',
  'packages.err.nameTooLong': 'Use 60 characters or fewer.',
  'packages.err.descriptionTooLong': 'Use 1,000 characters or fewer.',
  'packages.err.contactDetails': 'Contact details and links are not allowed in packages.',
  'packages.err.itemsCount': 'A package needs 2 to 6 of your active services',
  'packages.err.itemIneligible': '{name} can’t be added: {reason}',
  'packages.err.priceNotBelowTotal': 'Package price must be lower than the services’ total',
  'packages.err.priceRequired': 'Enter a package price above AED 0.',
  'packages.err.percentRange': 'The discount must be a whole number between 5% and 70%.',
  'packages.err.limitReached': 'You can have up to 10 active packages',
  'packages.err.unavailable': 'This package is no longer available',
  'packages.err.startPast': 'The package cannot start before today.',
  'packages.err.endBeforeStart': 'The end date must be on or after the start date.',
  'packages.err.endPast': 'The end date cannot be before today.',
  'packages.err.window': 'With both dates set, a package runs for up to 90 days.',
  'packages.err.storageKey': 'We could not verify the uploaded image. Please upload it again.',
  'packages.err.stateChanged': 'This package changed, reload and try again.',
  'packages.err.transition': 'This action is not possible in the package’s current state. Reload to see the latest.',
  'packages.err.notFound': 'This package no longer exists.',
  'packages.err.forbidden': 'Forbidden',
  'packages.err.generic': 'Something went wrong. Please try again.',
  'packages.err.fixBelow': 'Please fix the highlighted fields.',
};

const ar: Dict = {
  'packages.tab.offers': 'العروض',
  'packages.tab.packages': 'الباقات',
  'packages.tab.label': 'نوع الترويج',
  'packages.title': 'الباقات',
  'packages.subtitle': 'اجمع من 2 إلى 6 من خدماتك بسعر واحد. تراجع كراجات كل باقة جديدة قبل نشرها.',
  'packages.new': 'باقة جديدة',
  'packages.loading': 'جارٍ تحميل الباقات…',
  'packages.loadFailed': 'تعذّر تحميل باقاتك. حاول مرة أخرى.',
  'packages.retry': 'حاول مرة أخرى',
  'packages.empty.title': 'لا توجد باقات بعد',
  'packages.empty.body': 'اجمع خدمات يحجزها العملاء معًا، مثل الغسيل والتلميع وتنظيف المقصورة، بسعر باقة واحد.',
  'packages.empty.cta': 'أنشئ باقتك الأولى',
  'packages.empty.needServices': 'تحتاج إلى خدمتين نشطتين على الأقل لإنشاء باقة. أضف خدمات أو فعّلها أولًا.',
  'packages.empty.goServices': 'الذهاب إلى الخدمات',
  'packages.filter.label': 'الحالة',
  'packages.filter.all': 'الكل',
  'packages.filter.ARCHIVED': 'المؤرشفة',
  'packages.filter.empty': 'لا توجد باقات بهذه الحالة.',
  'packages.itemsCount': '{n} خدمات',
  'packages.col.order': 'الترتيب',
  'packages.col.name': 'الباقة',
  'packages.col.price': 'السعر',
  'packages.col.status': 'الحالة',
  'packages.col.window': 'المدة',
  'packages.col.actions': 'الإجراءات',
  'packages.reorder.hint': 'تظهر الباقات الجارية في ملف كراجك بهذا الترتيب.',
  'packages.reorder.disabledHint': 'اعرض كل الحالات لتغيير الترتيب.',
  'packages.reorder.failed': 'تعذّر حفظ الترتيب الجديد. حاول مرة أخرى.',
  'packages.status.DRAFT': 'مسودة',
  'packages.status.PENDING_APPROVAL': 'بانتظار الموافقة',
  'packages.status.SCHEDULED': 'مجدولة',
  'packages.status.LIVE': 'نشطة',
  'packages.status.PAUSED': 'متوقفة مؤقتًا',
  'packages.status.ENDED': 'منتهية',
  'packages.status.REJECTED': 'مرفوضة',
  'packages.status.SUSPENDED': 'معلّقة',
  'packages.mode.FIXED': 'سعر ثابت',
  'packages.mode.PERCENT': 'خصم بالنسبة',
  'packages.price.percentOff': 'خصم {pct}٪',
  'packages.window.always': 'حتى تنهيها',
  'packages.unavailable.badge': 'غير متاحة',
  'packages.reason.INACTIVE': 'الخدمة غير نشطة',
  'packages.reason.PENDING_APPROVAL': 'الخدمة بانتظار الموافقة',
  'packages.reason.REJECTED': 'الخدمة مرفوضة',
  'packages.reason.BLOCKED': 'الخدمة محظورة من كراجات',
  'packages.reason.HIDDEN': 'الخدمة مخفية',
  'packages.reason.NOT_PRICED': 'الخدمة بلا سعر',
  'packages.reason.CHOICE_REMOVED': 'أُزيل خيار ثابت',
  'packages.reason.PRICE_NOT_BELOW_TOTAL': 'لم يعد سعر الباقة أقل من مجموع الخدمات',
  'packages.reason.GARAGE_BLOCKED': 'كراجك محظور',
  'packages.reason.OTHER': 'غير متاحة',
  'packages.rejectReason.CONTACT_DETAILS': 'بيانات تواصل أو رابط خارجي على الصورة',
  'packages.rejectReason.PRICE_MISMATCH': 'السعر على الصورة يختلف عن سعر الباقة',
  'packages.rejectReason.UNREADABLE_IMAGE': 'صورة غير واضحة أو منخفضة الجودة',
  'packages.rejectReason.MISLEADING_CLAIM': 'ادعاء مضلل',
  'packages.rejectReason.COMPETITOR_BRANDING': 'علامة تجارية لمنافس',
  'packages.rejectReason.ARABIC_MISSING': 'النص العربي غير موجود',
  'packages.rejectReason.OTHER': 'سبب آخر',
  'packages.action.edit': 'تعديل',
  'packages.action.view': 'عرض',
  'packages.action.pause': 'إيقاف مؤقت',
  'packages.action.resume': 'استئناف',
  'packages.action.end': 'إنهاء الآن',
  'packages.action.archive': 'أرشفة',
  'packages.action.withdraw': 'سحب',
  'packages.confirm.cancel': 'إلغاء',
  'packages.confirm.pause.title': 'إيقاف هذه الباقة مؤقتًا؟',
  'packages.confirm.pause.body': 'لن يراها العملاء ولن يتمكنوا من حجزها. يمكنك استئنافها في أي وقت.',
  'packages.confirm.resume.title': 'استئناف هذه الباقة؟',
  'packages.confirm.resume.body': 'سيتمكن العملاء من رؤية الباقة وحجزها مجددًا.',
  'packages.confirm.end.title': 'إنهاء هذه الباقة الآن؟',
  'packages.confirm.end.body': 'تتوقف الباقة فورًا ولا يمكن إعادة تشغيلها. تحتفظ الحجوزات السابقة بسعرها.',
  'packages.confirm.archive.title': 'أرشفة هذه الباقة؟',
  'packages.confirm.archive.body': 'ستُنقل من قائمتك. يمكنك العثور عليها ضمن فلتر المؤرشفة.',
  'packages.confirm.withdraw.title': 'سحب من المراجعة؟',
  'packages.confirm.withdraw.body': 'تعود الباقة مسودة لتعديلها وإرسالها مجددًا.',
  'packages.toast.updated': 'تم تحديث الباقة',
  'packages.editor.newTitle': 'باقة جديدة',
  'packages.editor.editTitle': 'تعديل الباقة',
  'packages.editor.back': 'العودة إلى الباقات',
  'packages.editor.loadFailed': 'تعذّر تحميل هذه الباقة.',
  'packages.section.details': 'التفاصيل',
  'packages.section.cover': 'صورة الغلاف',
  'packages.section.services': 'الخدمات',
  'packages.section.price': 'السعر',
  'packages.section.window': 'التواريخ',
  'packages.field.nameEn': 'الاسم (بالإنجليزية)',
  'packages.field.nameAr': 'الاسم (بالعربية)',
  'packages.field.descriptionEn': 'الوصف (بالإنجليزية)',
  'packages.field.descriptionAr': 'الوصف (بالعربية)',
  'packages.field.optional': 'اختياري',
  'packages.field.startsAt': 'تاريخ البدء',
  'packages.field.endsAt': 'تاريخ الانتهاء',
  'packages.hint.arabic': 'العربية اختيارية. يرى العملاء النص الإنجليزي عند تركها فارغة.',
  'packages.hint.window': 'اختياري. اترك التاريخين فارغين لتستمر الباقة حتى توقفها أو تنهيها. 90 يومًا كحد أقصى عند تحديد التاريخين.',
  'packages.cover.label': 'صورة الغلاف',
  'packages.cover.hint': 'تظهر على بطاقة الباقة في التطبيق.',
  'packages.picker.search': 'ابحث في خدماتك',
  'packages.picker.selected': 'تم اختيار {n} من 6',
  'packages.picker.hint': 'اختر من 2 إلى 6 من خدماتك النشطة.',
  'packages.picker.loading': 'جارٍ تحميل خدماتك…',
  'packages.picker.loadFailed': 'تعذّر تحميل خدماتك.',
  'packages.picker.empty': 'لا توجد لديك خدمات بعد.',
  'packages.picker.noMatch': 'لا توجد خدمة تطابق بحثك.',
  'packages.picker.max': 'يمكن أن تضم الباقة 6 خدمات كحد أقصى.',
  'packages.picker.remove': 'إزالة {name}',
  'packages.picker.from': 'من',
  'packages.picker.unavailable': 'لا يمكن إضافتها: {reason}',
  'packages.fixer.title': 'خيارات {name}',
  'packages.fixer.customerPicks': 'يختار العميل',
  'packages.fixer.noOptions': 'بلا خيارات: سعر واحد.',
  'packages.fixer.carSpecific': 'حسب السيارة',
  'packages.fixer.carSpecificHint': 'يعتمد السعر على سيارة العميل، لذا تستخدم هذه الباقة خصمًا بالنسبة.',
  'packages.fixer.multiHint': 'ثبّت خيارًا أو أكثر، أو اتركه للعميل.',
  'packages.fixer.maxValues': 'يمكنك تثبيت 10 خيارات كحد أقصى لكل خدمة.',
  'packages.price.modeLabel': 'التسعير',
  'packages.price.modeHint.FIXED': 'كل الخيارات ثابتة، لذا تحدد سعرًا واحدًا للباقة.',
  'packages.price.modeHint.PERCENT': 'بعض الخيارات متروكة للعميل، أو يعتمد السعر على السيارة، لذا تحدد نسبة خصم.',
  'packages.price.originalTotal': 'مجموع الخدمات',
  'packages.price.packagePrice': 'سعر الباقة (درهم)',
  'packages.price.saves': 'يوفّر العميل {amount} ({pct}٪)',
  'packages.price.percent': 'الخصم (٪)',
  'packages.price.percentHint': 'بين 5٪ و70٪ مما يختاره العميل.',
  'packages.price.from': 'يرى العملاء «ابتداءً من {amount}»',
  'packages.price.checking': 'جارٍ التحقق من الأسعار…',
  'packages.price.previewFailed': 'تعذّر التحقق من الأسعار الآن. سيُعاد التحقق عند الحفظ.',
  'packages.price.addServices': 'أضف خدمتين على الأقل لرؤية المجموع.',
  'packages.bar.saveDraft': 'حفظ المسودة',
  'packages.bar.saveChanges': 'حفظ التعديلات',
  'packages.bar.submit': 'إرسال للموافقة',
  'packages.bar.saving': 'جارٍ الحفظ…',
  'packages.bar.saved': 'تم حفظ المسودة',
  'packages.bar.changesSaved': 'تم حفظ التعديلات. يراها العملاء الآن.',
  'packages.bar.submitted': 'تم الإرسال للموافقة',
  'packages.bar.withdrawn': 'تم السحب. عادت الباقة مسودة.',
  'packages.bar.submitNeedsItems': 'أضف خدمتين على الأقل للإرسال.',
  'packages.notice.pending': 'بانتظار موافقة كراجات. اسحب الباقة إذا احتجت إلى تعديلها.',
  'packages.notice.rejected': 'لم توافق كراجات على هذه الباقة',
  'packages.notice.rejectedHint': 'عالج الملاحظات أدناه ثم أرسلها مجددًا.',
  'packages.notice.suspended': 'علّقت كراجات هذه الباقة',
  'packages.notice.suspendedHint': 'الباقة غير ظاهرة للعملاء. عدّلها ثم أرسلها مجددًا.',
  'packages.notice.running': 'حالة هذه الباقة: {status}. تُطبّق التعديلات التي تحفظها فورًا بعد التحقق نفسه.',
  'packages.notice.ended': 'انتهت هذه الباقة، ويُحتفظ بها في سجلاتك.',
  'packages.notice.unavailable': 'لا يستطيع العملاء رؤية هذه الباقة أو حجزها الآن: {reason}. عالج ذلك لتصبح متاحة مجددًا.',
  'packages.adminEdited': 'آخر تعديل من كراجات في {date}',
  'packages.preview.title': 'معاينة التطبيق',
  'packages.preview.language': 'لغة المعاينة',
  'packages.preview.theme': 'مظهر المعاينة',
  'packages.preview.en': 'الإنجليزية',
  'packages.preview.ar': 'العربية',
  'packages.preview.light': 'فاتح',
  'packages.preview.dark': 'داكن',
  'packages.err.nameRequired': 'أدخل اسمًا بالإنجليزية.',
  'packages.err.nameTooLong': 'استخدم 60 حرفًا أو أقل.',
  'packages.err.descriptionTooLong': 'استخدم 1,000 حرف أو أقل.',
  'packages.err.contactDetails': 'لا يُسمح ببيانات التواصل أو الروابط في الباقات.',
  'packages.err.itemsCount': 'تحتاج الباقة إلى 2 إلى 6 من خدماتك النشطة',
  'packages.err.itemIneligible': 'لا يمكن إضافة {name}: {reason}',
  'packages.err.priceNotBelowTotal': 'يجب أن يكون سعر الباقة أقل من مجموع الخدمات',
  'packages.err.priceRequired': 'أدخل سعرًا للباقة أكبر من 0 درهم.',
  'packages.err.percentRange': 'يجب أن يكون الخصم عددًا صحيحًا بين 5٪ و70٪.',
  'packages.err.limitReached': 'يمكنك امتلاك 10 باقات نشطة كحد أقصى',
  'packages.err.unavailable': 'هذه الباقة لم تعد متاحة',
  'packages.err.startPast': 'لا يمكن أن تبدأ الباقة قبل اليوم.',
  'packages.err.endBeforeStart': 'يجب أن يكون تاريخ الانتهاء في يوم البدء أو بعده.',
  'packages.err.endPast': 'لا يمكن أن يكون تاريخ الانتهاء قبل اليوم.',
  'packages.err.window': 'عند تحديد التاريخين، تستمر الباقة 90 يومًا كحد أقصى.',
  'packages.err.storageKey': 'تعذّر التحقق من الصورة المرفوعة. ارفعها مرة أخرى.',
  'packages.err.stateChanged': 'تغيّرت هذه الباقة، أعد التحميل وحاول مرة أخرى.',
  'packages.err.transition': 'لا يمكن تنفيذ هذا الإجراء في حالة الباقة الحالية. أعد التحميل لرؤية آخر تحديث.',
  'packages.err.notFound': 'هذه الباقة لم تعد موجودة.',
  'packages.err.forbidden': 'غير مسموح',
  'packages.err.generic': 'حدث خطأ ما. حاول مرة أخرى.',
  'packages.err.fixBelow': 'يرجى تصحيح الحقول المحددة.',
};

export const PACKAGES_DICT: Record<'en' | 'ar', Dict> = { en, ar };

export type PackagesT = (key: string, vars?: Record<string, string | number>) => string;

function interpolate(text: string, vars?: Record<string, string | number>): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

/** `t` for the packages screens: DB translation first, then the bundled EN/AR string. */
export function usePackagesT(): { t: PackagesT; lang: 'en' | 'ar'; dir: 'ltr' | 'rtl' } {
  const { t: dbT, lang, dir } = useLanguage();
  const t = useCallback<PackagesT>((key, vars) => {
    const fromDb = dbT(key);
    const text = fromDb && fromDb !== key ? fromDb : (PACKAGES_DICT[lang]?.[key] ?? PACKAGES_DICT.en[key] ?? key);
    return interpolate(text, vars);
  }, [dbT, lang]);
  return { t, lang, dir };
}

/** Ineligible / unavailable reason text, with a generic fallback for codes this build does not know. */
export function reasonText(t: PackagesT, reason: string | null | undefined): string {
  if (!reason) return t('packages.reason.OTHER');
  const key = `packages.reason.${reason}`;
  const text = t(key);
  return text === key ? t('packages.reason.OTHER') : text;
}

/** Admin reject reason code (same picker as offers), or the raw code when this build does not know it. */
export function rejectReasonText(t: PackagesT, code: string): string {
  const key = `packages.rejectReason.${code}`;
  const text = t(key);
  return text === key ? code : text;
}
